import { useCallback, useEffect, useState } from "react";
import { History, Search, Download, Calendar, X, CheckCircle, Clock, AlertCircle, MapPin } from "lucide-react";
import { apiUrl, assetUrl, getAuthToken } from "../api";
import { dayStart, dayEnd, todayInput, daysAgoInput } from "../dateRange";

interface Lot {
    id: number;
    name: string;
}

interface Row {
    id: number;
    license_plate: string;
    owner_name: string;
    owner_email: string;
    lot_name: string | null;
    entry_time: string;
    exit_time: string | null;
    fee: number;
    status: "IN" | "paid" | "unpaid";
    exit_source: "ONLINE" | "OFFLINE_SYNC" | "MANUAL" | null;
    override_by: string | null;
    entry_image_url: string | null;
    exit_image_url: string | null;
}

interface Summary {
    sessions: number;
    active: number;
    revenue: number;
    unpaid_total: number;
}

const STATUS = {
    IN: { text: "Đang gửi", cls: "bg-amber-100 text-amber-800", Icon: Clock },
    paid: { text: "Đã thanh toán", cls: "bg-emerald-100 text-emerald-800", Icon: CheckCircle },
    unpaid: { text: "Chưa thanh toán", cls: "bg-red-100 text-red-800", Icon: AlertCircle },
};

const fmtTime = (s: string | null) =>
    s ? new Date(s).toLocaleString("vi-VN", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }) : "---";

const money = (n: number) => `${n.toLocaleString("vi-VN")}₫`;

// Lịch sử gửi xe toàn hệ thống: lọc theo bãi xe, ngày, trạng thái, tìm kiếm; xuất Excel đúng bộ lọc đang chọn.
export function AdminHistoryPage() {
    const [lots, setLots] = useState<Lot[]>([]);
    const [lotId, setLotId] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [status, setStatus] = useState("");
    const [search, setSearch] = useState("");
    const [query, setQuery] = useState("");
    const [rows, setRows] = useState<Row[]>([]);
    const [summary, setSummary] = useState<Summary | null>(null);
    const [limit, setLimit] = useState(500);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        fetch(apiUrl("/parking-lots"))
            .then((r) => (r.ok ? r.json() : []))
            .then(setLots)
            .catch(() => setLots([]));
    }, []);

    // Gõ tìm kiếm xong 300ms mới gọi API.
    useEffect(() => {
        const t = setTimeout(() => setQuery(search.trim()), 300);
        return () => clearTimeout(t);
    }, [search]);

    const params = useCallback(() => {
        const p = new URLSearchParams();
        if (lotId) p.set("lot_id", lotId);
        const from = dayStart(fromDate);
        const to = dayEnd(toDate);
        if (from) p.set("from", from.toISOString());
        if (to) p.set("to", to.toISOString());
        if (status) p.set("status", status);
        if (query) p.set("q", query);
        return p;
    }, [lotId, fromDate, toDate, status, query]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const res = await fetch(apiUrl(`/admin/history?${params()}`), { headers: { Authorization: `Bearer ${getAuthToken()}` } });
                const data = await res.json();
                if (cancelled) return;
                if (!res.ok) {
                    setError(res.status === 401 ? "Phiên đăng nhập đã hết hạn. Vui lòng đăng xuất và đăng nhập lại." : data.message || "Lỗi tải lịch sử");
                    return;
                }
                setError("");
                setRows(data.rows);
                setSummary(data.summary);
                setLimit(data.limit);
            } catch {
                if (!cancelled) setError("Không thể kết nối đến server");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [params]);

    const exportExcel = async () => {
        setExporting(true);
        try {
            const res = await fetch(apiUrl(`/admin/history/export?${params()}`), { headers: { Authorization: `Bearer ${getAuthToken()}` } });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                setError(data.message || "Lỗi xuất báo cáo");
                return;
            }
            const blob = await res.blob();
            const name = /filename="([^"]+)"/.exec(res.headers.get("content-disposition") || "")?.[1] || "lich-su-gui-xe.xlsx";
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = name;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch {
            setError("Không thể kết nối đến server");
        } finally {
            setExporting(false);
        }
    };

    const isFiltered = !!(lotId || fromDate || toDate || status || search);
    const clearFilters = () => {
        setLotId("");
        setFromDate("");
        setToDate("");
        setStatus("");
        setSearch("");
    };

    const cards = [
        { title: "Tổng lượt gửi", value: summary ? summary.sessions.toLocaleString("vi-VN") : "...", color: "from-cyan-500 to-cyan-600" },
        { title: "Đang gửi", value: summary ? summary.active.toLocaleString("vi-VN") : "...", color: "from-amber-500 to-amber-600" },
        { title: "Doanh thu đã thu", value: summary ? money(summary.revenue) : "...", color: "from-emerald-500 to-emerald-600" },
        { title: "Chưa thanh toán", value: summary ? money(summary.unpaid_total) : "...", color: "from-red-500 to-red-600" },
    ];

    return (
        <div className="space-y-6">
            <div className="bg-gradient-to-r from-cyan-600 via-blue-600 to-cyan-800 rounded-2xl p-4 lg:p-8 text-white shadow-2xl flex items-center justify-between">
                <div>
                    <h1 className="text-2xl lg:text-3xl font-bold mb-2">Lịch sử gửi xe</h1>
                    <p className="text-cyan-100 text-base lg:text-lg">Toàn bộ lượt gửi xe trong hệ thống</p>
                </div>
                <div className="bg-white bg-opacity-20 p-3 lg:p-4 rounded-full">
                    <History className="h-6 w-6 lg:h-8 lg:w-8" />
                </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {cards.map((c) => (
                    <div key={c.title} className="bg-white rounded-2xl p-5 shadow-lg border border-gray-100">
                        <p className="text-sm text-gray-600 mb-1">{c.title}</p>
                        <p className={`text-xl lg:text-2xl font-bold bg-gradient-to-r ${c.color} bg-clip-text text-transparent`}>{c.value}</p>
                    </div>
                ))}
            </div>

            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-4 lg:px-6 py-4 border-b border-gray-200 space-y-3">
                    <div className="flex flex-col lg:flex-row lg:items-end gap-3">
                        <label className="text-sm text-gray-700 lg:w-56">
                            <span className="block mb-1 flex items-center gap-1"><MapPin className="h-4 w-4" /> Bãi gửi xe</span>
                            <select value={lotId} onChange={(e) => setLotId(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white">
                                <option value="">Tất cả bãi xe</option>
                                {lots.map((l) => (
                                    <option key={l.id} value={l.id}>{l.name}</option>
                                ))}
                            </select>
                        </label>
                        <label className="text-sm text-gray-700">
                            <span className="block mb-1 flex items-center gap-1"><Calendar className="h-4 w-4" /> Từ ngày</span>
                            <input type="date" value={fromDate} max={toDate || todayInput()} onChange={(e) => setFromDate(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white" />
                        </label>
                        <label className="text-sm text-gray-700">
                            <span className="block mb-1">Đến ngày</span>
                            <input type="date" value={toDate} min={fromDate || undefined} max={todayInput()} onChange={(e) => setToDate(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white" />
                        </label>
                        <label className="text-sm text-gray-700 lg:w-44">
                            <span className="block mb-1">Trạng thái</span>
                            <select value={status} onChange={(e) => setStatus(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white">
                                <option value="">Tất cả</option>
                                <option value="IN">Đang gửi</option>
                                <option value="paid">Đã thanh toán</option>
                                <option value="unpaid">Chưa thanh toán</option>
                            </select>
                        </label>
                        <label className="text-sm text-gray-700 flex-1">
                            <span className="block mb-1">Tìm kiếm</span>
                            <div className="relative">
                                <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Biển số, tên hoặc email" className="w-full border border-gray-300 rounded-lg pl-9 pr-3 py-2 bg-white" />
                            </div>
                        </label>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <button onClick={() => { const t = todayInput(); setFromDate(t); setToDate(t); }} className="text-sm border border-gray-300 bg-white rounded-lg px-3 py-1.5 hover:bg-gray-50">Hôm nay</button>
                        <button onClick={() => { setFromDate(daysAgoInput(6)); setToDate(todayInput()); }} className="text-sm border border-gray-300 bg-white rounded-lg px-3 py-1.5 hover:bg-gray-50">7 ngày qua</button>
                        <button onClick={() => { setFromDate(daysAgoInput(29)); setToDate(todayInput()); }} className="text-sm border border-gray-300 bg-white rounded-lg px-3 py-1.5 hover:bg-gray-50">30 ngày qua</button>
                        {isFiltered && (
                            <button onClick={clearFilters} className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1 px-2 py-1.5">
                                <X className="h-4 w-4" /> Bỏ lọc
                            </button>
                        )}
                        <button
                            onClick={exportExcel}
                            disabled={exporting || !summary?.sessions}
                            className="ml-auto bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg flex items-center gap-2 font-medium"
                        >
                            <Download className="h-4 w-4" />
                            {exporting ? "Đang xuất..." : "Xuất báo cáo (Excel)"}
                        </button>
                    </div>
                </div>

                {error && <div className="mx-4 mt-4 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">{error}</div>}

                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                {["Biển số", "Chủ xe", "Bãi xe", "Thời gian vào", "Thời gian ra", "Chi phí", "Trạng thái", "Ảnh"].map((h) => (
                                    <th key={h} className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {loading ? (
                                <tr>
                                    <td colSpan={8} className="py-12 text-center">
                                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-cyan-600 mx-auto"></div>
                                    </td>
                                </tr>
                            ) : rows.length === 0 ? (
                                <tr>
                                    <td colSpan={8} className="px-6 py-12 text-center text-gray-500">
                                        <History className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                                        <p className="text-lg font-medium">{isFiltered ? "Không có lượt gửi xe phù hợp bộ lọc" : "Chưa có lịch sử gửi xe"}</p>
                                    </td>
                                </tr>
                            ) : (
                                rows.map((r) => {
                                    const st = STATUS[r.status];
                                    return (
                                        <tr key={r.id} className="hover:bg-gray-50">
                                            <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{r.license_plate}</td>
                                            <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm text-gray-900">{r.owner_name}</div>
                                                <div className="text-xs text-gray-500">{r.owner_email}</div>
                                            </td>
                                            <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-700">{r.lot_name || "-"}</td>
                                            <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-900">{fmtTime(r.entry_time)}</td>
                                            <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-900">{fmtTime(r.exit_time)}</td>
                                            <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{r.status === "IN" ? "---" : money(r.fee)}</td>
                                            <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                                                <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-full ${st.cls}`}>
                                                    <st.Icon className="h-3.5 w-3.5" /> {st.text}
                                                </span>
                                                {r.exit_source === "OFFLINE_SYNC" && <div className="mt-1 text-xs text-amber-700">Ghi nhận khi mất mạng</div>}
                                                {r.exit_source === "MANUAL" && r.override_by && <div className="mt-1 text-xs text-gray-500">Mở tay: {r.override_by}</div>}
                                            </td>
                                            <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                                                <div className="flex gap-2">
                                                    {[["Vào", r.entry_image_url], ["Ra", r.exit_image_url]].map(([label, url]) => {
                                                        const src = assetUrl(url);
                                                        return src ? (
                                                            <a key={label} href={src} target="_blank" rel="noreferrer" title={`Ảnh lúc ${label}`}>
                                                                <img src={src} alt={`Ảnh ${label}`} className="h-10 w-16 object-cover rounded border" />
                                                            </a>
                                                        ) : null;
                                                    })}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
                {summary && summary.sessions > rows.length && (
                    <p className="px-6 py-3 text-sm text-gray-500 border-t border-gray-100">
                        Đang hiển thị {rows.length.toLocaleString("vi-VN")} lượt gần nhất trên tổng {summary.sessions.toLocaleString("vi-VN")} (tối đa {limit}). Thu hẹp bộ lọc hoặc xuất Excel để xem đầy đủ.
                    </p>
                )}
            </div>
        </div>
    );
}
